import { type Scheme } from './defaults';

export type ColorSchemePreference = 'system' | 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Under `'system'` we follow the OS. Under an explicit `'light'`/`'dark'` we do not subscribe at all:
 * the merchant forced a scheme, and an OS change must not override it.
 */
export function resolveScheme(preference: ColorSchemePreference): Scheme {
  if (preference === 'light' || preference === 'dark') return preference;
  return prefersDark() ? 'dark' : 'light';
}

function prefersDark(): boolean {
  // `matchMedia` is absent in some older webviews and in non-browser test environments. A missing
  // API means "assume light" rather than throwing on a payment page.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

/**
 * Subscribes to OS scheme changes, but only under `'system'`. Returns a cleanup function; callers
 * must call it, because this is the one listener that outlives a render.
 */
export function subscribeToSchemeChanges(
  preference: ColorSchemePreference,
  onChange: (scheme: Scheme) => void,
): () => void {
  if (preference !== 'system') return () => undefined;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const query = window.matchMedia(DARK_QUERY);
  const handler = (event: MediaQueryListEvent) => {
    onChange(event.matches ? 'dark' : 'light');
  };

  query.addEventListener('change', handler);
  return () => {
    query.removeEventListener('change', handler);
  };
}
